// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useRef, useState, KeyboardEvent, ChangeEvent, ClipboardEvent, useEffect, FocusEvent, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Input, Stack, Typography } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { UnderDevelopment } from '~/components/common';
// Others
import { DEFAULT_LENGTH_OTP, EMPTY_STRING, REG_EXP } from '~/utils/constants/common';
import { ClipboardDataEnum, KeyboardEnum, VerifyTypeEnum } from '~/utils/enum/common';
import { IBodyVerifyCode, IVerifyCode } from '~/utils/interface/verifyCode';
import { useAppDispatch } from '~/utils/redux/hooks';
import { LoadingContext } from '~/context';
import { verifyCode } from '~/thunks/auth/authThunk';
import { defaultValuesForm, schema } from './helper';
import { validateWithRegex } from '~/utils/helper/common';
import { authRouteAbsolute } from '~/utils/routes/auth';
// Styles, images, icons
import styles from './VerifyCode.module.scss';
import './VerifyCode.scss';

type Props = {};

const cx = classNames.bind(styles);

const VerifyCode = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/auth', 'common/base']);
  const loadingContext = useContext(LoadingContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    control,
    trigger,
    setValue,
    getValues,
    handleSubmit,
    formState: { isValid },
  } = useForm<IVerifyCode>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  const currentCode = useWatch({ name: 'code', control });
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('id');
  const type = searchParams.get('type');
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [dataOTP, setDataOTP] = useState<string[]>(Array(DEFAULT_LENGTH_OTP).fill(EMPTY_STRING));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isFirstSubmit, setIsFirstSubmit] = useState<boolean>(false);
  const [hasErrorMessage, setHasErrorMessage] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (!isFirstSubmit) return;
    const isFullyFilled = Array.isArray(currentCode) && currentCode.every((item) => !!item);

    setHasErrorMessage(!isFullyFilled);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCode]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleRefInput = (element: HTMLInputElement, index: number) => {
    inputRefs.current[index] = element;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    if (
      !validateWithRegex(event.key, REG_EXP.IS_NUMBER) &&
      event.key !== KeyboardEnum.BACKSPACE &&
      event.key !== KeyboardEnum.DELETE &&
      event.key !== KeyboardEnum.TAB &&
      !event.metaKey
    ) {
      event.preventDefault();
    }

    if (event.key === KeyboardEnum.BACKSPACE || event.key === KeyboardEnum.DELETE) {
      if (event.currentTarget instanceof HTMLInputElement) {
        const index = inputRefs.current.indexOf(event.currentTarget);
        setValue(`code.${index}`, EMPTY_STRING, { shouldValidate: true });

        if (index > 0) {
          inputRefs.current[index - 1]?.focus();
          event.preventDefault();
          trigger();
        }
      }
    }
  };

  const handleChangeValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const { value } = event.target;
    const newValue = value.slice(-1);
    const currentValue = getValues(`code.${index}`);
    const currentCodeValues = Array.from({ length: DEFAULT_LENGTH_OTP }, (_, i) => getValues(`code.${i}`));

    const isFullyFilled = currentCodeValues.every((code) => !!code);

    if (isFullyFilled) {
      return;
    }

    if (REG_EXP.VERIFY_CODE.test(newValue)) {
      if (!currentValue) {
        setValue(`code.${index}`, newValue, { shouldValidate: true });
      } else {
        setValue(`code.${index + 1}`, newValue, { shouldValidate: true });
      }
      trigger();

      if (newValue && index < DEFAULT_LENGTH_OTP - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData(ClipboardDataEnum.TEXT);

    if (!validateWithRegex(text, REG_EXP.NUMBER_MAX_LENGTH_SIX)) {
      return;
    }

    const digits = text.split(EMPTY_STRING);
    setDataOTP(digits);
  };

  const handleFocusCode = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    const { value } = event.target;
    event.target.setSelectionRange(value.length, value.length);
  };

  const handleInputCodeClick = (index: number) => {
    const input = inputRefs.current[index];
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);
  };

  const handleResend = () => {
    setIsShowUnderDevelopment(true);
  };

  const handleVerifyCode = (data: IVerifyCode) => {
    const code = data.code.join(EMPTY_STRING);
    const isFullyFilled = data.code.every((code) => !!code);

    setIsFirstSubmit(true);

    if (!isFullyFilled || !isValid) {
      setHasErrorMessage(true);
      return;
    }

    setHasErrorMessage(false);

    loadingContext?.show();

    const payload: IBodyVerifyCode = {
      code: code,
    };

    dispatch(verifyCode(payload))
      .unwrap()
      .then((resp) => {
        if (!resp?.data) return;

        const { type, accountId } = resp?.data;

        if (type === VerifyTypeEnum.FORGOT_PASSWORD && accountId) {
          navigate(authRouteAbsolute.createNewPassword, {
            state: payload,
          });
          return;
        }
      })
      .catch((_error) => {})
      .finally(() => {
        loadingContext?.hide();
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  //#endregion Handle Function

  return (
    <Box id='verifyCodePage' className={cx('container')} component={'form'} onSubmit={handleSubmit(handleVerifyCode)}>
      <Stack direction={'column'} spacing={'5px'} alignItems={'center'}>
        <Typography className={cx('pageName')}>{t('verify_code_title')}</Typography>

        <Typography className={cx('description')}>{t('verify_code_description')}</Typography>
      </Stack>

      <Stack direction={'column'} spacing={'20px'} marginTop={'10px'}>
        <Box component={'div'} className={cx('inputContainer')}>
          <Box component={'div'} className={cx('inputWrapper')}>
            {dataOTP.map((_, index) => (
              <Controller
                key={index}
                name={`code.${index}`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    slotProps={{
                      root: {
                        className: cx('inputRoot'),
                      },
                      input: {
                        className: cx('input'),
                      },
                    }}
                    inputRef={(element: HTMLInputElement) => handleRefInput(element, index)}
                    autoFocus={index === 0}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                    onChange={(event) => handleChangeValue(event, index)}
                    onPaste={(event) => handlePaste(event)}
                    onFocus={(event) => handleFocusCode(event)}
                    onClick={() => handleInputCodeClick(index)}
                  />
                )}
              />
            ))}
          </Box>
          {hasErrorMessage && <Typography className={cx('errMessage')}>{t('validate_code_invalid')}</Typography>}
        </Box>

        <Button variant='contained' className={cx('buttonVerify')} type='submit' size='large'>
          {t('verify_code_button_verify')}
        </Button>
      </Stack>

      <Stack direction={'row'} justifyContent={'center'} gap={1}>
        <Typography component={'span'} className={cx('textSubTitle')}>
          {t('verify_code_sub_title')}
        </Typography>
        <Typography component={'span'} className={cx('buttonResend')} onClick={handleResend}>
          {t('verify_code_sub_description')}
        </Typography>
      </Stack>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleCloseUnderDevelopment} />}
    </Box>
  );
};

export default VerifyCode;
